import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, Button, HStack, Icon, Tabs, TabList, Tab, TabPanels, TabPanel, Text } from "@chakra-ui/react";
import { FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { HeaderWithStats } from "services/MetadataServices";
import { fetchAnalystList } from "services/serviceUtils";
import AnalysesTab from "components/sessionPage/AnalysesTab";
import AnalysisRun from "components/sessionPage/AnalysisRun";
import { AppIcons } from "constants/icons";
import ChatSessionListContainer from "modules/chat/containers/ChatSessionList";

const ChatSession: React.FC = () => {
  const navigate = useNavigate();

  // tab index state
  const [tabIndex, setTabIndex] = useState(0);

  // Analysts list is shared across Workflows and Workflow Runs tabs
  const [analystsList, setAnalystsList] = useState<HeaderWithStats[]>([]);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);

  const loadAnalysts = useCallback(async () => {
    setIsLoadingAnalysts(true);
    const list = await fetchAnalystList(false);
    setAnalystsList(list);
    setIsLoadingAnalysts(false);
  }, []);

  useEffect(() => {
    loadAnalysts();
  }, [loadAnalysts]);

  return (
    <Box h="100vh" w="100%" bg="white" overflow="hidden">
      <Flex h="100%" w="100%" justify="center">
        {/* Main Content */}
        <Box w="100%" maxW="1000px" h="100%" px={5} py={6} display="flex" flexDirection="column" minH={0}>
          <Tabs
            mt={6}
            flex={1}
            display="flex"
            flexDirection="column"
            minH={0}
            colorScheme="purple"
            variant="line"
            index={tabIndex}
            onChange={index => setTabIndex(index)}
          >
            {/* Tab Headers */}
            <Flex justify="space-between" align="center" mb={6}>
              <TabList border="none" gap={7}>
                <Tab
                  px={0}
                  pb={2}
                  fontSize="xl"
                  fontWeight="600"
                  color="gray.500"
                  _focus={{ bg: "transparent", boxShadow: "none" }}
                  _active={{ bg: "transparent" }}
                  _selected={{
                    color: "purple.700",
                    borderColor: "purple.700",
                    borderBottomWidth: "3px",
                    fontWeight: "700",
                  }}
                  _hover={{ color: "purple.700" }}
                >
                  <HStack spacing={2}>
                    <Icon as={AppIcons.chatSessions} boxSize={6} />
                    <Text fontSize="xl" fontWeight="600" mb={0}>
                      Chats
                    </Text>
                  </HStack>
                </Tab>
                <Tab
                  px={0}
                  pb={2}
                  fontSize="xl"
                  fontWeight="600"
                  color="gray.500"
                  _focus={{ bg: "transparent", boxShadow: "none" }}
                  _active={{ bg: "transparent" }}
                  _selected={{
                    color: "purple.700",
                    borderColor: "purple.700",
                    borderBottomWidth: "3px",
                    fontWeight: "700",
                  }}
                  _hover={{ color: "purple.700" }}
                >
                  <HStack spacing={2}>
                    <Icon as={AppIcons.workflow} boxSize={6} />
                    <Text fontSize="xl" fontWeight="600" mb={0}>
                      Workflows
                    </Text>
                  </HStack>
                </Tab>
                <Tab
                  px={0}
                  pb={2}
                  fontSize="xl"
                  fontWeight="600"
                  color="gray.500"
                  _focus={{ bg: "transparent", boxShadow: "none" }}
                  _active={{ bg: "transparent" }}
                  _selected={{
                    color: "purple.700",
                    borderColor: "purple.700",
                    borderBottomWidth: "3px",
                    fontWeight: "700",
                  }}
                  _hover={{ color: "purple.700" }}
                >
                  <HStack spacing={2}>
                    <Icon as={AppIcons.workflowRun} boxSize={6} />
                    <Text fontSize="xl" fontWeight="600" mb={0}>
                      Workflow Runs
                    </Text>
                  </HStack>
                </Tab>
              </TabList>
              <Button
                leftIcon={<Icon as={FiPlus} boxSize={5} />}
                bg="purple.700"
                color="white"
                size="md"
                fontWeight="600"
                borderRadius="md"
                _hover={{ bg: "purple.600" }}
                onClick={() => navigate("/chat")}
              >
                New chat
              </Button>
            </Flex>

            <TabPanels flex={1} display="flex" flexDirection="column" minH={0}>
              {/* Chats Tab */}
              <TabPanel px={0} py={0} flex={1} display="flex" flexDirection="column" minH={0}>
                <ChatSessionListContainer analystsList={analystsList} />
              </TabPanel>

              {/* Analyses Tab */}
              <TabPanel px={0} py={0} flex={1} display="flex" flexDirection="column" minH={0}>
                <AnalysesTab
                  isTabActive={tabIndex === 1}
                  analystsList={analystsList}
                  isLoadingAnalysts={isLoadingAnalysts}
                />
              </TabPanel>
              {/* Analysis Outputs Tab */}
              <TabPanel px={0} py={0}>
                <Box h="calc(100vh - 200px)" overflowY="hidden">
                  <AnalysisRun analystsList={analystsList} isTabActive={tabIndex === 2 ? true : false} />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Flex>
    </Box>
  );
};

export default ChatSession;
